import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { UserState } from "@/context/User/UserReducer";
import { updateDesignation } from "@/services/Designation.service";
import {
	Designation,
	UpdateDesignationParams,
} from "@/types/designation.types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "../ui/form";

const formSchema = z.object({
	designationid: z.number(),
	designation: z.string(),
});

const EditDesignation: React.FC<{
	user: UserState | null;
	designation: Designation;
}> = ({ user, designation }) => {
	const [open, setOpen] = useState(false);

	const queryClient = useQueryClient();

	const editDesignation = useMutation({
		mutationKey: ["designation_update", designation.designationid],
		mutationFn: (data: UpdateDesignationParams) =>
			updateDesignation(user?.accessToken!, data),
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({
				queryKey: ["designation", designation.designationid.toString()],
				exact: true,
				refetchType: "all",
			});
			setOpen(false);
			form.reset();
			toast.success("Success", {
				description: `Designation ${variables.designation} has been successfully created`,
			});
			editDesignation.reset();
		},
		onError: (err) => {
			toast.error("Error!", { description: "Error adding reporter, try again" });
			console.log(err);
		},
	});

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			designationid: designation.designationid,
			designation: designation.designation,
		},
	});

	const onSubmit = (data: z.infer<typeof formSchema>) => {
		console.log(data);
		editDesignation.mutate(data);
	};

	const disabled = () => {
		if (editDesignation.isPending) return true;
		if (editDesignation.isSuccess) return true;
		return false;
	};

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>
				<Button variant="default">Edit Designation</Button>
			</DialogTrigger>
			<DialogContent className="sm:max-w-xl">
				<DialogHeader>
					<DialogTitle className="text-left font-bold text-2xl">
						Edit Designation
					</DialogTitle>
				</DialogHeader>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)}>
						<FormField
							name="designationid"
							render={({ field }) => (
								<FormItem className="flex-1 mb-2">
									<FormLabel>Reporter ID</FormLabel>
									<FormControl>
										<Input
											type="number"
											defaultValue={designation.designationid}
											onChange={(e) => field.onChange(e.target.valueAsNumber)}
											disabled
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							name="designation"
							render={({ field }) => (
								<FormItem className="flex-1 mb-2">
									<FormLabel>Name of Designation</FormLabel>
									<FormControl>
										<Input type="text" {...field} disabled={disabled()} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<Button
							type="submit"
							className="mt-4 w-full"
							disabled={editDesignation.isPending || editDesignation.isSuccess}
						>
							{editDesignation.isPending ? "Updating..." : "Edit Designation"}
						</Button>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	);
};

export default EditDesignation;
